import { useState } from 'react'

import { useGetPublicSessionTimeoutQuery } from '@/core/services/session-settings'
import { useIdleLogout } from '@/hooks/useIdleLogout'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { logout, selectIsAuthenticated } from '@/redux/reducers/auth.reducer'

const IDLE_WARNING_DURATION_MS = 60_000
const DEFAULT_INACTIVITY_TIMEOUT_MINUTES = 15

/**
 * Logs the user out after the establishment's inactivity timeout, showing a countdown warning
 * during the last minute. Returns the warning dialog state and its "stay connected" handler.
 */
export function useIdleWarning() {
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const { data: sessionTimeout } = useGetPublicSessionTimeoutQuery()
  const [open, setOpen] = useState(false)
  const [seconds, setSeconds] = useState(Math.round(IDLE_WARNING_DURATION_MS / 1000))

  const inactivityTimeoutMs = (sessionTimeout?.inactivityTimeoutMinutes ?? DEFAULT_INACTIVITY_TIMEOUT_MINUTES) * 60 * 1000
  const { reset } = useIdleLogout({
    timeoutMs: inactivityTimeoutMs,
    enabled: isAuthenticated,
    onLogout: () => dispatch(logout()),
    warningMs: IDLE_WARNING_DURATION_MS,
    onWarn: (remainingMs) => {
      setSeconds(Math.max(1, Math.round(remainingMs / 1000)))
      setOpen(true)
    },
    onActive: () => setOpen(false),
  })

  const onStay = () => {
    setOpen(false)
    reset()
  }

  return { open, seconds, onStay }
}
